import React, {
  ExoticComponent,
  Fragment,
  useMemo,
  useRef,
  useState
} from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Calendar,
  CalendarWithDate,
  List,
  NoIcon,
  TextAa,
  TreeStructure
} from '@siftscience/focus-components/icons'
import { Text } from '@siftscience/focus-components/text'
import { Theme } from '@material-ui/core'
import { startCase } from 'lodash'
import Action from '../../action'
import { DataField, DataFieldType, JSONSchema } from '../../../dto/template'
import { Popover } from '@siftscience/focus-components/popover'
import { useDataFieldContext } from '../../../context/data-field-context'
import { useTemplateContext } from '../../../context/template-context'
import { Modal } from '@siftscience/focus-components/modal'
import { Portal } from '../../../../../components/Portal'
import { EditableSchemaTable } from '../../../EditableSchemaTable'
import { ButtonVariant } from '@siftscience/focus-components/button'
import { Action as ModalAction } from '@siftscience/focus-components/action-bar'
import {
  NONE_MERCHANT,
  useMerchantContext
} from '../../../context/merchant-context'

interface StylesProps {
  selected: boolean
  required: boolean
}

const useStyles = makeStyles<Theme, StylesProps>(() => ({
  datafield: {
    width: 'fit-content',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: ({ required, selected }) =>
      selected ? '#FFE07A' : required ? '#FFEDEC' : '#FFF6D6',
    border: ({ required, selected }) =>
      selected
        ? '1px solid #8F5B02'
        : required
        ? '1px solid #EB3B2E'
        : '1px solid #F2B200',
    borderRadius: '4px',
    padding: '2px',
    cursor: 'pointer',
    margin: '5px 0',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,

    '& svg': {
      width: '16px',
      height: '16px'
    }
  }
}))

const MODAL_ACTION_LABEL = {
  confirm: 'Confirm',
  cancel: 'Cancel'
}

const MODAL_ACTIONS: ModalAction[] = [
  { label: MODAL_ACTION_LABEL.confirm },
  {
    label: MODAL_ACTION_LABEL.cancel,
    variant: 'secondary' as ButtonVariant,
    lined: true
  }
]

const DfComponent = ({ node, selected, editor, getPos }) => {
  const { activeTemplateData, gpdTemplateData, setUpdatedTemplateContent } =
    useTemplateContext()
  const { selectedMerchant } = useMerchantContext()
  const { addDataFieldToTemplate, addedDataFields } = useDataFieldContext()
  const popoverOnClickRef = useRef(null)
  const { dfName } = node.attrs // dfName is actually a dataset_path which is unique in the scope of 1 template
  const editorTemplateId = editor.storage.template.id
  const isActiveTemplate = activeTemplateData?.templateId === editorTemplateId
  const [openConfigurationModal, setOpenConfigurationModal] = useState(false)
  const [dataValue, setDataValue] = useState<Partial<DataField>>()

  const dataField = useMemo(() => {
    if (!isActiveTemplate) {
      const templateDataField = gpdTemplateData?.dataFields?.find(
        df => df.dataset_path === dfName
      )
      if (templateDataField) {
        return templateDataField
      }
    } else {
      if (!activeTemplateData?.dataFields?.length && !addedDataFields?.length) {
        return {}
      }
      const overridenDataField = addedDataFields.find(
        df => df.dataset_path === dfName
      )
      if (overridenDataField) {
        return overridenDataField
      }
      const templateDataField = activeTemplateData?.dataFields.find(
        df => df.dataset_path === dfName
      )
      if (templateDataField) {
        return templateDataField
      }
    }

    return {}
  }, [activeTemplateData, addedDataFields, isActiveTemplate])
  const classes = useStyles({ selected, required: dataField.required })

  const Icon: ExoticComponent<{
    children?: React.ReactNode
    className?: string
    color?: string
  }> = useMemo(() => {
    if (dataField?.dataType === DataFieldType.TEXT) {
      return TextAa
    } else if (dataField?.dataType === DataFieldType.IMAGE) {
      return NoIcon
    } else if (dataField?.dataType === DataFieldType.DATE) {
      return Calendar
    } else if (dataField?.dataType === DataFieldType.DATETIME) {
      return CalendarWithDate
    } else if (dataField?.dataType === DataFieldType.BOOLEAN) {
      return TreeStructure
    } else if (dataField?.dataType === DataFieldType.ARRAY) {
      return List
    }
    return Fragment
  }, [dataField])

  const onSaveDataField = (editedDataField: Partial<DataField>) => {
    const dataFieldToPass = {
      id: dataField.id,
      ...editedDataField
    }
    addDataFieldToTemplate(dataFieldToPass, true)
    setUpdatedTemplateContent(prevState => ({ ...prevState, isDirty: true }))
  }

  const onSaveSchema = (data: JSONSchema) => {
    const dataWithSchema = {
      ...dataField,
      schema: data
    }
    setDataValue(dataWithSchema)
  }

  const onModalAction = (action: ModalAction) => {
    if (action.label === MODAL_ACTION_LABEL.cancel) {
      setOpenConfigurationModal(false)
      return
    }

    if (action.label === MODAL_ACTION_LABEL.confirm) {
      onSaveDataField(dataValue)
      setOpenConfigurationModal(false)
    }
  }

  const isReadonly = useMemo(() => {
    if (!isActiveTemplate) {
      // this is gpd of the selected override
      return true
    }

    if (
      isActiveTemplate &&
      !!activeTemplateData.templateId &&
      !gpdTemplateData.templateId &&
      !activeTemplateData.merchantSpecific &&
      selectedMerchant.id !== NONE_MERCHANT.id
    ) {
      // Merchant is selected and viewing gpd without override
      return true
    }

    return false
  }, [activeTemplateData, gpdTemplateData, isActiveTemplate])

  return (
    <NodeViewWrapper
      className={`df-node ${selected ? 'Tiptap-selected' : ''}`}
      as="span"
      data-drag-handle={true}
    >
      {isReadonly ? (
        <Text size="small" color="primary" className={classes.datafield}>
          <Icon /> {startCase(dataField.name)}
        </Text>
      ) : (
        <>
          <Portal>
            <Modal
              scrollable={true}
              visible={openConfigurationModal}
              variant="fixed"
              title={'Array fields'}
              onCloseButtonClick={() => setOpenConfigurationModal(false)}
              onClickOutside={() => setOpenConfigurationModal(false)}
              size="large"
              actions={MODAL_ACTIONS}
              onAction={onModalAction}
            >
              <EditableSchemaTable
                schema={dataField.schema}
                fieldName={dfName.replace(/^\$\./, '')}
                onSchemaChange={onSaveSchema}
              />
            </Modal>
          </Portal>

          <Popover
            placement="bottom"
            content={() => (
              <Action
                isNewDataField={false}
                setOpenConfigurationModal={setOpenConfigurationModal}
                defaultDataType={DataFieldType.TEXT}
                defaultDataField={dataField}
                onCancel={event => {
                  if (popoverOnClickRef.current) {
                    popoverOnClickRef.current(event)
                  }
                }}
                onSave={(editedDataField, event) => {
                  if (popoverOnClickRef.current) {
                    popoverOnClickRef.current(event)
                  }
                  if (editedDataField.dataset_path !== dfName) {
                    editor.view.dispatch(
                      editor.view.state.tr.setNodeMarkup(getPos(), null, {
                        ...node.attrs,
                        dfName: editedDataField.dataset_path
                      })
                    )
                  }
                  onSaveDataField(editedDataField)
                }}
              />
            )}
          >
            {props => {
              popoverOnClickRef.current = props.onClick
              return (
                <Text
                  size="small"
                  color="primary"
                  onClick={props.onClick}
                  className={classes.datafield}
                  {...props}
                >
                  <Icon /> {startCase(dataField.name)}
                </Text>
              )
            }}
          </Popover>
        </>
      )}
    </NodeViewWrapper>
  )
}

export default DfComponent
